import { WagmiAdapter } from "@reown/appkit-adapter-wagmi";
import type { AppKitNetwork } from "@reown/appkit/networks";
import type { Chain, Transport } from "viem";
import { fallback, http } from "viem";
import { createConfig } from "wagmi";

import appConfig from "configs/app";
import multichainConfig from "configs/multichain";
import { currentChain, parentChain, clusterChains } from "lib/web3/chains";

const feature = appConfig.features.blockchainInteraction;

const chains = [currentChain, parentChain, ...(clusterChains ?? [])].filter(
  Boolean,
);

const getChainTransportFromConfig = (
  config: typeof appConfig,
  readOnly?: boolean,
): Record<string, Transport> => {
  if (!config.chain.id) {
    return {};
  }

  const urls = config.chain.rpcUrls
    .concat(readOnly ? `${config.apis.general.endpoint}/api/eth-rpc` : "")
    .filter(Boolean);

  // 如果没有可用的 RPC URL，返回空对象，不创建 transport
  if (urls.length === 0) {
    console.warn(`No RPC URLs configured for chain ${config.chain.id}`);
    return {};
  }

  return {
    [config.chain.id]: fallback(
      urls.map((url) => http(url, { batch: { wait: 100 } })),
    ),
  };
};

const reduceClusterChainsToTransportConfig = (
  readOnly: boolean,
): Record<string, Transport> => {
  const config = multichainConfig();

  if (!config) {
    return {};
  }

  return config.chains
    .map(({ config }) => getChainTransportFromConfig(config, readOnly))
    .reduce(
      (result, item) => {
        Object.entries(item).map(([id, transport]) => {
          result[id] = transport;
        });
        return result;
      },
      {} as Record<string, Transport>,
    );
};

const wagmi = (() => {
  if (!feature.isEnabled) {
    const transports: Record<string, Transport> = {
      ...getChainTransportFromConfig(appConfig, true),
      ...reduceClusterChainsToTransportConfig(true),
    };

    // 只有在 parentChain 有有效 RPC URL 时才添加
    if (parentChain && parentChain.rpcUrls?.default?.http?.[0]) {
      transports[parentChain.id] = http(parentChain.rpcUrls.default.http[0]);
    }

    const wagmiConfig = createConfig({
      chains: chains as [Chain, ...Array<Chain>],
      transports,
      ssr: true,
      batch: { multicall: { wait: 100 } },
    });

    return { config: wagmiConfig, adapter: null };
  }

  const transports: Record<string, Transport> = {
    ...getChainTransportFromConfig(appConfig, false),
    ...reduceClusterChainsToTransportConfig(false),
  };

  // 只有在 parentChain 有有效 RPC URL 时才添加
  if (parentChain && parentChain.rpcUrls?.default?.http?.[0]) {
    transports[parentChain.id] = http(parentChain.rpcUrls.default.http[0]);
  }

  const wagmiAdapter = new WagmiAdapter({
    networks: chains as Array<AppKitNetwork>,
    multiInjectedProviderDiscovery: true,
    transports,
    projectId: feature.walletConnect.projectId,
    ssr: true,
    batch: { multicall: { wait: 100 } },
    syncConnectedChain: false,
  });

  return { config: wagmiAdapter.wagmiConfig, adapter: wagmiAdapter };
})();

export default wagmi;
